import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  AlertTriangle,
  FileText,
  Target,
  Shield,
  Settings,
  Users,
  Radio,
  MapPin,
  Syringe,
  Building,
  Building2,
  UserCheck,
  MessageSquare,
  Headphones,
  ListChecks,
  AlertCircle,
  ShieldAlert,
  Camera,
  Upload,
  Plus,
  Trash2,
  Edit,
  X,
  Play,
  Printer,
  Save,
  Map
} from 'lucide-react';
import { API_BASE_URL } from '../config';
import secureStorage from '../utils/secureStorage';
import useImageDrop from '../utils/useImageDrop';

/**
 * ExCon Builder - Exercise Control Builder (16 Steps)
 * Uses the same forms and cards as the Exercise Builder
 * Step 8 (Injections) is the primary control mechanism
 */
const ExConBuilder = ({ onBack, hiraEntry = null }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState({});
  
  // Exercise types - same as Exercise Builder
  const exerciseTypes = [
    'Tabletop Exercise',
    'Functional Exercise', 
    'Full-Scale Exercise',
    'Drill',
    'Workshop',
    'Seminar',
    'Game'
  ];

  // Validation errors
  const [exerciseValidationErrors, setExerciseValidationErrors] = useState({
    latitude: '',
    longitude: '',
    zoom_level: ''
  });
  const [scenarioValidationErrors, setScenarioValidationErrors] = useState({
    latitude: '',
    longitude: ''
  });
  const [organizationValidationErrors, setOrganizationValidationErrors] = useState({
    latitude: '',
    longitude: ''
  });

  // Exercise data state - matches Exercise Builder structure
  const [exerciseData, setExerciseData] = useState({
    // Step 1: Exercise
    exercise_image: null,
    exercise_name: hiraEntry?.name ? `ExCon - ${hiraEntry.name}` : '',
    exercise_type: 'Tabletop Exercise',
    exercise_description: hiraEntry?.description || '',
    location: '',
    latitude: hiraEntry?.latitude != null && !isNaN(parseFloat(hiraEntry.latitude)) ? parseFloat(hiraEntry.latitude).toFixed(4) : '',
    longitude: hiraEntry?.longitude != null && !isNaN(parseFloat(hiraEntry.longitude)) ? parseFloat(hiraEntry.longitude).toFixed(4) : '',
    zoom_level: 8,
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    // Step 2: Scope
    scope_description: '',
    scope_hazards: hiraEntry?.name || '',
    scope_geographic_area: '',
    scope_functions: '',
    scope_organizations: '',
    scope_personnel: '',
    // Step 3: Purpose
    purpose_description: '',
    // Step 4: Scenario
    scenario_image: null,
    scenario_name: '',
    scenario_description: hiraEntry?.description || '',
    scenario_latitude: hiraEntry?.latitude != null && !isNaN(parseFloat(hiraEntry.latitude)) ? parseFloat(hiraEntry.latitude).toFixed(4) : '',
    scenario_longitude: hiraEntry?.longitude != null && !isNaN(parseFloat(hiraEntry.longitude)) ? parseFloat(hiraEntry.longitude).toFixed(4) : '',
    // ExCon specific
    hira_id: hiraEntry?.id || null,
    is_excon: true,
    excon_lead: ''
  });

  // Dynamic lists - same structure as Exercise Builder
  const [goals, setGoals] = useState([]);
  const [currentGoal, setCurrentGoal] = useState({ name: '', description: '' });
  const [editingGoalId, setEditingGoalId] = useState(null);

  const [objectives, setObjectives] = useState([]);
  const [currentObjective, setCurrentObjective] = useState({ name: '', description: '' });
  const [editingObjectiveId, setEditingObjectiveId] = useState(null);

  const [functions, setFunctions] = useState([]);
  const [currentFunction, setCurrentFunction] = useState({ name: '', description: '' });
  const [editingFunctionId, setEditingFunctionId] = useState(null);

  // MSEL Injection state - matches Exercise Builder Step 8
  const [injections, setInjections] = useState([]);
  const [currentInjection, setCurrentInjection] = useState({ 
    event_number: 1,
    scenario_time: '',
    event_type: '',
    inject_mode: '',
    from_entity: '',
    to_entity: '',
    message: '',
    expected_response: '',
    objective_capability_task: '',
    notes: ''
  });
  const [editingInjectionId, setEditingInjectionId] = useState(null);

  // MSEL dropdown options - same as Exercise Builder
  const eventTypes = [
    'Player Inject',
    'Media Inject',
    'Discussion Question',
    'Information Update',
    'Decision Point',
    'Time Advance',
    'Scenario Development',
    'Resource Request',
    'Communication Test',
    'Evaluation Checkpoint'
  ];

  const injectModes = [
    'Phone Call',
    'Radio Communication',
    'Email/Message',
    'Face-to-Face',
    'Written Document',
    'Media Broadcast',
    'Social Media',
    'Emergency Alert System',
    'Controller Briefing',
    'Automated System'
  ];

  const controllerRoles = [
    'Exercise Controller',
    'Lead Controller',
    'Safety Officer',
    'Evaluator',
    'SIMCELL Controller',
    'Communications Controller',
    'Media Controller',
    'Logistics Controller',
    'Technical Controller',
    'Observer Controller'
  ];

  const playerRoles = [
    'Incident Commander',
    'Operations Section',
    'Planning Section',
    'Logistics Section',
    'Finance/Administration Section',
    'Safety Officer',
    'Public Information Officer',
    'Liaison Officer',
    'All Players',
    'Emergency Operations Center',
    'Field Teams',
    'Media Representatives'
  ];

  const [organizations, setOrganizations] = useState([]);
  const [currentOrganization, setCurrentOrganization] = useState({ 
    name: '', description: '', home_base: '', latitude: '', longitude: '',
    contact_first_name: '', contact_last_name: '', contact_phone: '', contact_email: '',
    organization_image: null
  });
  const [editingOrganizationId, setEditingOrganizationId] = useState(null);
  const [organizationImagePreview, setOrganizationImagePreview] = useState(null);

  const [codeWords, setCodeWords] = useState([]);
  const [currentCodeWord, setCurrentCodeWord] = useState({ word: '', meaning: '' });
  const [editingCodeWordId, setEditingCodeWordId] = useState(null);

  const [callsigns, setCallsigns] = useState([]);
  const [currentCallsign, setCurrentCallsign] = useState({ callsign: '', description: '' });
  const [editingCallsignId, setEditingCallsignId] = useState(null);

  const [frequencies, setFrequencies] = useState([]);
  const [currentFrequency, setCurrentFrequency] = useState({ 
    frequency: '', type: '', description: '', primary_backup: 'Primary',
    tone: '', offset: '', channel: '', radio_type: '', modulation_type: ''
  });
  const [editingFrequencyId, setEditingFrequencyId] = useState(null);

  const [assumptions, setAssumptions] = useState([]);
  const [currentAssumption, setCurrentAssumption] = useState({ assumption: '', description: '' });
  const [editingAssumptionId, setEditingAssumptionId] = useState(null);

  const [artificialities, setArtificialities] = useState([]);
  const [currentArtificiality, setCurrentArtificiality] = useState({ artificiality: '', description: '' });
  const [editingArtificialityId, setEditingArtificialityId] = useState(null);

  const [safetyConcerns, setSafetyConcerns] = useState([]);
  const [currentSafetyConcern, setCurrentSafetyConcern] = useState({ concern: '', description: '', safety_officer: '', phone: '' });
  const [editingSafetyConcernId, setEditingSafetyConcernId] = useState(null);

  const [coordinators, setCoordinators] = useState([]);

  // Safety Officer state - matches Exercise Builder
  const [safetyOfficer, setSafetyOfficer] = useState({ participantId: null, firstName: '', lastName: '', cellPhone: '' });
  const [safetyOfficerLoading, setSafetyOfficerLoading] = useState(false);

  // ExCon Team members fetched from Participants
  const [exconTeamMembers, setExconTeamMembers] = useState([]);
  const [exconTeamLoading, setExconTeamLoading] = useState(false);

  const steps = [
    { number: 1, title: 'Exercise', description: 'Basic exercise information', icon: FileText },
    { number: 2, title: 'Scope', description: 'Define exercise scope', icon: Target },
    { number: 3, title: 'Purpose', description: 'Exercise purpose and intent', icon: Shield },
    { number: 4, title: 'Scenario', description: 'Exercise scenario details', icon: AlertTriangle },
    { number: 5, title: 'Goals', description: 'Exercise goals and outcomes', icon: Target },
    { number: 6, title: 'Objectives', description: 'Specific objectives to test', icon: ListChecks },
    { number: 7, title: 'Functions', description: 'Functions to be exercised', icon: Settings },
    { number: 8, title: 'Injections', description: 'MSEL - Primary Control', icon: Syringe },
    { number: 9, title: 'Organizations', description: 'Participating organizations', icon: Building2 },
    { number: 10, title: 'Team Coordinators', description: 'Exercise coordination team', icon: UserCheck },
    { number: 11, title: 'Code Words', description: 'Exercise code words', icon: MessageSquare },
    { number: 12, title: 'Callsigns', description: 'Communication callsigns', icon: Headphones },
    { number: 13, title: 'Assumptions', description: 'Exercise assumptions', icon: AlertCircle },
    { number: 14, title: 'Artificialities', description: 'Artificial constraints', icon: Settings },
    { number: 15, title: 'Safety', description: 'Safety considerations', icon: Shield },
    { number: 16, title: 'Mapping', description: 'Create map objects', icon: MapPin }
  ];

  // Pre-populate from HIRA entry
  useEffect(() => {
    if (hiraEntry) {
      setExerciseData(prev => ({
        ...prev,
        exercise_name: `ExCon - ${hiraEntry.name}`,
        exercise_description: `Exercise Control for ${hiraEntry.name} - ${hiraEntry.disaster_type}`,
        scope_hazards: hiraEntry.name,
        scenario_description: hiraEntry.description || '',
        latitude: hiraEntry.latitude != null && !isNaN(parseFloat(hiraEntry.latitude)) ? parseFloat(hiraEntry.latitude).toFixed(4) : '',
        longitude: hiraEntry.longitude != null && !isNaN(parseFloat(hiraEntry.longitude)) ? parseFloat(hiraEntry.longitude).toFixed(4) : '',
        scenario_latitude: hiraEntry.latitude != null && !isNaN(parseFloat(hiraEntry.latitude)) ? parseFloat(hiraEntry.latitude).toFixed(4) : '',
        scenario_longitude: hiraEntry.longitude != null && !isNaN(parseFloat(hiraEntry.longitude)) ? parseFloat(hiraEntry.longitude).toFixed(4) : '',
        hira_id: hiraEntry.id
      }));
    }
  }, [hiraEntry]);

  // Fetch ExCon team members from Participants
  useEffect(() => {
    const fetchExconTeam = async () => {
      if (!hiraEntry?.id) return;
      setExconTeamLoading(true);
      try {
        const token = secureStorage.getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const [participantsRes, exercisesRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/participants`, { headers }),
          axios.get(`${API_BASE_URL}/api/exercise-builder`, { headers })
        ]);

        const participants = participantsRes.data || [];
        const exercises = exercisesRes.data || [];

        // Find exercise IDs linked to this HIRA
        const hiraExerciseIds = exercises
          .filter(ex => ex.hira_id === hiraEntry.id)
          .map(ex => ex.id);

        const targetPositions = ['exercise director', 'exercise evaluator', 'exercise observer'];

        // Filter participants assigned to HIRA-linked exercises with matching positions
        const team = participants.filter(p => {
          const pos = (p.position || '').toLowerCase();
          const assigned = p.assigned_exercise || '';
          return hiraExerciseIds.includes(assigned) && targetPositions.includes(pos);
        });

        setExconTeamMembers(team);
      } catch (error) {
        console.error('Error fetching ExCon team members:', error);
      } finally {
        setExconTeamLoading(false);
      }
    };

    fetchExconTeam();
  }, [hiraEntry]);

  // Validation functions - same as Exercise Builder
  const validateLatitude = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const num = parseFloat(value);
    if (isNaN(num)) return 'Must be a valid number';
    if (num < -90 || num > 90) return 'Must be between -90 and 90';
    return '';
  };

  const validateLongitude = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const num = parseFloat(value);
    if (isNaN(num)) return 'Must be a valid number';
    if (num < -180 || num > 180) return 'Must be between -180 and 180';
    return '';
  };

  const handleLatitudeChange = (value, context) => {
    const error = validateLatitude(value);
    if (context === 'exercise') {
      setExerciseData(prev => ({ ...prev, latitude: value }));
      setExerciseValidationErrors(prev => ({ ...prev, latitude: error }));
    } else if (context === 'scenario') {
      setExerciseData(prev => ({ ...prev, scenario_latitude: value }));
      setScenarioValidationErrors(prev => ({ ...prev, latitude: error }));
    } else if (context === 'organization') {
      setCurrentOrganization(prev => ({ ...prev, latitude: value }));
      setOrganizationValidationErrors(prev => ({ ...prev, latitude: error }));
    }
  };

  const handleLongitudeChange = (value, context) => {
    const error = validateLongitude(value);
    if (context === 'exercise') {
      setExerciseData(prev => ({ ...prev, longitude: value }));
      setExerciseValidationErrors(prev => ({ ...prev, longitude: error }));
    } else if (context === 'scenario') {
      setExerciseData(prev => ({ ...prev, scenario_longitude: value }));
      setScenarioValidationErrors(prev => ({ ...prev, longitude: error }));
    } else if (context === 'organization') {
      setCurrentOrganization(prev => ({ ...prev, longitude: value }));
      setOrganizationValidationErrors(prev => ({ ...prev, longitude: error }));
    }
  };

  const handleImageUpload = (event, imageType) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target.result;
        setImagePreview(prev => ({ ...prev, [imageType]: imageData }));
        setExerciseData(prev => ({ ...prev, [imageType]: imageData }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOrganizationImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target.result;
        setOrganizationImagePreview(imageData);
        setCurrentOrganization(prev => ({ ...prev, organization_image: imageData }));
      };
      reader.readAsDataURL(file);
    }
  };

  const { dropProps: exconOrgImageDropProps, isDragging: isExconOrgImageDragging } = useImageDrop({
    onFile: (file) => handleOrganizationImageUpload({ target: { files: [file] } })
  });

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Goal handlers
  const addGoal = () => {
    if (currentGoal.name.trim()) {
      if (editingGoalId) {
        setGoals(prev => prev.map(item => 
          item.id === editingGoalId ? { ...currentGoal, id: editingGoalId } : item
        ));
        setEditingGoalId(null);
      } else {
        setGoals(prev => [...prev, { ...currentGoal, id: Date.now() }]);
      }
      setCurrentGoal({ name: '', description: '' });
    }
  };

  const editGoal = (goal) => {
    setCurrentGoal({ name: goal.name, description: goal.description });
    setEditingGoalId(goal.id);
  };

  const cancelGoalEdit = () => {
    setCurrentGoal({ name: '', description: '' });
    setEditingGoalId(null);
  };

  // Objective handlers
  const addObjective = () => {
    if (currentObjective.name.trim()) {
      if (editingObjectiveId) {
        setObjectives(prev => prev.map(obj => 
          obj.id === editingObjectiveId ? { ...currentObjective, id: editingObjectiveId } : obj
        ));
        setEditingObjectiveId(null);
      } else {
        setObjectives(prev => [...prev, { ...currentObjective, id: Date.now() }]);
      }
      setCurrentObjective({ name: '', description: '' });
    }
  };

  const editObjective = (objective) => {
    setCurrentObjective({ name: objective.name, description: objective.description });
    setEditingObjectiveId(objective.id);
  };

  const cancelObjectiveEdit = () => {
    setCurrentObjective({ name: '', description: '' });
    setEditingObjectiveId(null);
  };

  // Function handlers
  const addFunction = () => {
    if (currentFunction.name.trim()) {
      if (editingFunctionId) {
        setFunctions(prev => prev.map(item => 
          item.id === editingFunctionId ? { ...currentFunction, id: editingFunctionId } : item
        ));
        setEditingFunctionId(null);
      } else {
        setFunctions(prev => [...prev, { ...currentFunction, id: Date.now() }]);
      }
      setCurrentFunction({ name: '', description: '' });
    }
  };

  const editFunction = (func) => {
    setCurrentFunction({ name: func.name, description: func.description });
    setEditingFunctionId(func.id);
  };

  const cancelFunctionEdit = () => {
    setCurrentFunction({ name: '', description: '' });
    setEditingFunctionId(null);
  };

  // Injection handlers (ExCon primary control) - matches MSEL form
  const addInjection = () => {
    if (currentInjection.message.trim()) {
      if (editingInjectionId) {
        setInjections(prev => prev.map(item => 
          item.id === editingInjectionId ? { ...currentInjection, id: editingInjectionId } : item
        ));
        setEditingInjectionId(null);
      } else {
        const nextEventNumber = injections.length > 0 
          ? Math.max(...injections.map(i => i.event_number || 0)) + 1 
          : 1;
        setInjections(prev => [...prev, { ...currentInjection, id: Date.now(), event_number: nextEventNumber }]);
      }
      setCurrentInjection({ 
        event_number: injections.length + 2,
        scenario_time: '',
        event_type: '',
        inject_mode: '',
        from_entity: '',
        to_entity: '',
        message: '',
        expected_response: '',
        objective_capability_task: '',
        notes: ''
      });
    }
  };

  const editInjection = (injection) => {
    setCurrentInjection({
      event_number: injection.event_number || 1,
      scenario_time: injection.scenario_time || '',
      event_type: injection.event_type || '',
      inject_mode: injection.inject_mode || '',
      from_entity: injection.from_entity || '',
      to_entity: injection.to_entity || '',
      message: injection.message || '',
      expected_response: injection.expected_response || '',
      objective_capability_task: injection.objective_capability_task || '',
      notes: injection.notes || ''
    });
    setEditingInjectionId(injection.id);
  };

  const cancelInjectionEdit = () => {
    setCurrentInjection({ 
      event_number: injections.length + 1,
      scenario_time: '',
      event_type: '',
      inject_mode: '',
      from_entity: '',
      to_entity: '',
      message: '',
      expected_response: '',
      objective_capability_task: '',
      notes: ''
    });
    setEditingInjectionId(null);
  };

  // Organization handlers
  const addOrganization = () => {
    if (currentOrganization.name.trim()) {
      if (editingOrganizationId) {
        setOrganizations(prev => prev.map(item => 
          item.id === editingOrganizationId ? { ...currentOrganization, id: editingOrganizationId } : item
        ));
        setEditingOrganizationId(null);
      } else {
        setOrganizations(prev => [...prev, { ...currentOrganization, id: Date.now() }]);
      }
      setCurrentOrganization({ 
        name: '', description: '', home_base: '', latitude: '', longitude: '',
        contact_first_name: '', contact_last_name: '', contact_phone: '', contact_email: '',
        organization_image: null
      });
      setOrganizationValidationErrors({ latitude: '', longitude: '' });
      setOrganizationImagePreview(null);
    }
  };

  const editOrganization = (org) => {
    setCurrentOrganization({
      name: org.name,
      description: org.description || '',
      home_base: org.home_base || '',
      latitude: org.latitude || '',
      longitude: org.longitude || '',
      contact_first_name: org.contact_first_name || '',
      contact_last_name: org.contact_last_name || '',
      contact_phone: org.contact_phone || '',
      contact_email: org.contact_email || '',
      organization_image: org.organization_image || null
    });
    setEditingOrganizationId(org.id);
    setOrganizationImagePreview(org.organization_image || null);
  };

  const cancelOrganizationEdit = () => {
    setCurrentOrganization({ 
      name: '', description: '', home_base: '', latitude: '', longitude: '',
      contact_first_name: '', contact_last_name: '', contact_phone: '', contact_email: '',
      organization_image: null
    });
    setEditingOrganizationId(null);
    setOrganizationValidationErrors({ latitude: '', longitude: '' });
    setOrganizationImagePreview(null);
  };

  // Code Word handlers
  const addCodeWord = () => {
    if (currentCodeWord.word.trim()) {
      if (editingCodeWordId) {
        setCodeWords(prev => prev.map(item => 
          item.id === editingCodeWordId ? { ...currentCodeWord, id: editingCodeWordId } : item
        ));
        setEditingCodeWordId(null);
      } else {
        setCodeWords(prev => [...prev, { ...currentCodeWord, id: Date.now() }]);
      }
      setCurrentCodeWord({ word: '', meaning: '' });
    }
  };

  const editCodeWord = (cw) => {
    setCurrentCodeWord({ word: cw.word, meaning: cw.meaning || '' });
    setEditingCodeWordId(cw.id);
  };

  const cancelCodeWordEdit = () => {
    setCurrentCodeWord({ word: '', meaning: '' });
    setEditingCodeWordId(null);
  };

  // Callsign handlers
  const addCallsign = () => {
    if (currentCallsign.callsign.trim()) {
      if (editingCallsignId) {
        setCallsigns(prev => prev.map(item => 
          item.id === editingCallsignId ? { ...currentCallsign, id: editingCallsignId } : item
        ));
        setEditingCallsignId(null);
      } else {
        setCallsigns(prev => [...prev, { ...currentCallsign, id: Date.now() }]);
      }
      setCurrentCallsign({ callsign: '', description: '' });
    }
  };

  const editCallsign = (cs) => {
    setCurrentCallsign({ callsign: cs.callsign, description: cs.description || '' });
    setEditingCallsignId(cs.id);
  };

  const cancelCallsignEdit = () => {
    setCurrentCallsign({ callsign: '', description: '' });
    setEditingCallsignId(null);
  };

  // Frequency handlers
  const addFrequency = () => {
    if (currentFrequency.frequency.trim()) {
      if (editingFrequencyId) {
        setFrequencies(prev => prev.map(item => 
          item.id === editingFrequencyId ? { ...currentFrequency, id: editingFrequencyId } : item
        ));
        setEditingFrequencyId(null);
      } else {
        setFrequencies(prev => [...prev, { ...currentFrequency, id: Date.now() }]);
      }
      setCurrentFrequency({ 
        frequency: '', type: '', description: '', primary_backup: 'Primary',
        tone: '', offset: '', channel: '', radio_type: '', modulation_type: ''
      });
    }
  };

  const editFrequency = (freq) => {
    setCurrentFrequency({
      frequency: freq.frequency,
      type: freq.type || '',
      description: freq.description || '',
      primary_backup: freq.primary_backup || 'Primary',
      tone: freq.tone || '',
      offset: freq.offset || '',
      channel: freq.channel || '',
      radio_type: freq.radio_type || '',
      modulation_type: freq.modulation_type || ''
    });
    setEditingFrequencyId(freq.id);
  };

  const cancelFrequencyEdit = () => {
    setCurrentFrequency({ 
      frequency: '', type: '', description: '', primary_backup: 'Primary',
      tone: '', offset: '', channel: '', radio_type: '', modulation_type: ''
    });
    setEditingFrequencyId(null);
  };

  // Assumption handlers
  const addAssumption = () => {
    if ((currentAssumption.assumption || '').trim()) {
      if (editingAssumptionId) {
        setAssumptions(prev => prev.map(item => 
          item.id === editingAssumptionId ? { ...currentAssumption, id: editingAssumptionId } : item
        ));
        setEditingAssumptionId(null);
      } else {
        setAssumptions(prev => [...prev, { ...currentAssumption, id: Date.now() }]);
      }
      setCurrentAssumption({ assumption: '', description: '' });
    }
  };

  const editAssumption = (a) => {
    setCurrentAssumption({ assumption: a.assumption || '', description: a.description || '' });
    setEditingAssumptionId(a.id);
  };

  const cancelAssumptionEdit = () => {
    setCurrentAssumption({ assumption: '', description: '' });
    setEditingAssumptionId(null);
  };

  // Artificiality handlers
  const addArtificiality = () => {
    if ((currentArtificiality.artificiality || '').trim()) {
      if (editingArtificialityId) {
        setArtificialities(prev => prev.map(item => 
          item.id === editingArtificialityId ? { ...currentArtificiality, id: editingArtificialityId } : item
        ));
        setEditingArtificialityId(null);
      } else {
        setArtificialities(prev => [...prev, { ...currentArtificiality, id: Date.now() }]);
      }
      setCurrentArtificiality({ artificiality: '', description: '' });
    }
  };

  const editArtificiality = (a) => {
    setCurrentArtificiality({ artificiality: a.artificiality || '', description: a.description || '' });
    setEditingArtificialityId(a.id);
  };

  const cancelArtificialityEdit = () => {
    setCurrentArtificiality({ artificiality: '', description: '' });
    setEditingArtificialityId(null);
  };

  // Safety Concern handlers
  const addSafetyConcern = () => {
    if ((currentSafetyConcern.concern || '').trim()) {
      if (editingSafetyConcernId) {
        setSafetyConcerns(prev => prev.map(item => 
          item.id === editingSafetyConcernId ? { ...currentSafetyConcern, id: editingSafetyConcernId } : item
        ));
        setEditingSafetyConcernId(null);
      } else {
        setSafetyConcerns(prev => [...prev, { ...currentSafetyConcern, id: Date.now() }]);
      }
      setCurrentSafetyConcern({ concern: '', description: '', safety_officer: '', phone: '' });
    }
  };

  const editSafetyConcern = (sc) => {
    setCurrentSafetyConcern({ concern: sc.concern || '', description: sc.description || '', safety_officer: sc.safety_officer || '', phone: sc.phone || '' });
    setEditingSafetyConcernId(sc.id);
  };

  const cancelSafetyConcernEdit = () => {
    setCurrentSafetyConcern({ concern: '', description: '', safety_officer: '', phone: '' });
    setEditingSafetyConcernId(null);
  };

  // Delete handlers
  const deleteGoal = (id) => setGoals(prev => prev.filter(item => item.id !== id));
  const deleteObjective = (id) => setObjectives(prev => prev.filter(item => item.id !== id));
  const deleteFunction = (id) => setFunctions(prev => prev.filter(item => item.id !== id));
  const deleteInjection = (id) => setInjections(prev => prev.filter(item => item.id !== id));
  const deleteOrganization = (id) => setOrganizations(prev => prev.filter(item => item.id !== id));
  const deleteCodeWord = (id) => setCodeWords(prev => prev.filter(item => item.id !== id));
  const deleteCallsign = (id) => setCallsigns(prev => prev.filter(item => item.id !== id));
  const deleteFrequency = (id) => setFrequencies(prev => prev.filter(item => item.id !== id));
  const deleteAssumption = (id) => setAssumptions(prev => prev.filter(item => item.id !== id));
  const deleteArtificiality = (id) => setArtificialities(prev => prev.filter(item => item.id !== id));
  const deleteSafetyConcern = (id) => setSafetyConcerns(prev => prev.filter(item => item.id !== id));

  // Load Coordinators - matches Exercise Builder
  const loadCoordinators = async () => {
    setLoading(true);
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_BASE_URL}/api/participants`, { headers });
      const allParticipants = response.data || [];
      const coordinatorParticipants = allParticipants.filter(p => 
        p.position?.toLowerCase().includes('coordinator') || 
        p.role?.toLowerCase().includes('coordinator')
      );
      setCoordinators(coordinatorParticipants.map(p => ({
        id: p.id || Date.now() + Math.random(),
        name: `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.name || 'Unknown',
        role: p.position || p.role || 'Team Coordinator',
        email: p.email || '',
        phone: p.cell_phone || p.phone || '',
        assignedTo: p.assigned_exercise || 'N/A'
      })));
    } catch (error) {
      console.error('Error loading coordinators:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load Safety Officer - matches Exercise Builder
  const loadSafetyOfficer = async () => {
    setSafetyOfficerLoading(true);
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_BASE_URL}/api/participants`, { headers });
      const allParticipants = response.data || [];
      const safetyOfficerParticipant = allParticipants.find(p => 
        p.position?.toLowerCase().includes('safety officer') || 
        p.role?.toLowerCase().includes('safety officer')
      );
      if (safetyOfficerParticipant) {
        setSafetyOfficer({
          participantId: safetyOfficerParticipant.id,
          firstName: safetyOfficerParticipant.first_name || '',
          lastName: safetyOfficerParticipant.last_name || '',
          cellPhone: safetyOfficerParticipant.cell_phone || safetyOfficerParticipant.phone || ''
        });
      }
    } catch (error) {
      console.error('Error loading safety officer:', error);
    } finally {
      setSafetyOfficerLoading(false);
    }
  };

  // Save exercise
  const saveExercise = async () => {
    setLoading(true);
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Convert date strings to proper ISO datetime format (matches Exercise Builder)
      let startDate, endDate;

      if (exerciseData.start_date) {
        startDate = new Date(exerciseData.start_date + 'T' + (exerciseData.start_time || '00:00'));
        if (isNaN(startDate.getTime())) startDate = new Date();
      } else {
        startDate = new Date();
      }

      if (exerciseData.end_date) {
        endDate = new Date(exerciseData.end_date + 'T' + (exerciseData.end_time || '23:59'));
        if (isNaN(endDate.getTime())) {
          endDate = new Date(startDate);
          endDate.setDate(endDate.getDate() + 1);
        }
      } else {
        endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 1);
      }

      const exercisePayload = {
        ...exerciseData,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        latitude: parseFloat(exerciseData.latitude) || 0,
        longitude: parseFloat(exerciseData.longitude) || 0,
        scenario_latitude: parseFloat(exerciseData.scenario_latitude) || 0,
        scenario_longitude: parseFloat(exerciseData.scenario_longitude) || 0,
        goals,
        objectives,
        functions,
        injections,
        organizations,
        codeWords,
        callsigns,
        frequencies,
        assumptions,
        artificialities,
        safetyConcerns,
        coordinators,
        is_excon: true,
        hira_id: hiraEntry?.id || null
      };

      const response = await axios.post(`${API_BASE_URL}/api/exercise-builder`, exercisePayload, { headers });
      
      if (response.data && response.data.id) {
        alert('ExCon Exercise saved successfully!');
        window.location.hash = `#ics?exercise=${response.data.id}`;
      }
    } catch (error) {
      console.error('Error saving exercise:', error);
      alert('Failed to save exercise. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Image Upload Component - same as Exercise Builder
  const ImageUploadComponent = ({ imageType, title }) => {
    const { dropProps, isDragging } = useImageDrop({
      onFile: (file) => handleImageUpload({ target: { files: [file] } }, imageType)
    });
    return (
    <div className="flex items-center space-x-4">
      <div
        {...dropProps}
        onClick={() => document.getElementById(`excon-${imageType}-upload`)?.click()}
        data-testid={`excon-${imageType}-dropzone`}
        title="Click to browse or drop an image here"
        className={`w-32 h-32 bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden border-2 border-dashed cursor-pointer transition-colors ${
          isDragging ? 'border-orange-500 bg-orange-500/10 ring-2 ring-orange-500' : 'border-gray-600 hover:border-orange-500/50'
        }`}
      >
        {imagePreview[imageType] || exerciseData[imageType] ? (
          <img
            src={imagePreview[imageType] || exerciseData[imageType]}
            alt={title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="text-center text-gray-500 px-2">
            <Camera className="h-8 w-8 mx-auto mb-2" />
            <span className="text-xs">{isDragging ? 'Drop to upload' : 'No image · Drop here'}</span>
          </div>
        )}
      </div>
      <div className="space-y-2">
        <label className="cursor-pointer">
          <input
            id={`excon-${imageType}-upload`}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleImageUpload(e, imageType)}
          />
          <Button variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-500/10" asChild>
            <span><Upload className="h-4 w-4 mr-2" />Upload Photo</span>
          </Button>
        </label>
        <p className="text-xs text-gray-500">Upload {title.toLowerCase()} image — or drag &amp; drop</p>
      </div>
    </div>
  );
  };

  // Render step content - matches Exercise Builder patterns
  const renderStepContent = () => {
    switch (currentStep) {
      case 1: // Exercise - same as Exercise Builder Step 1
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Exercise Image</CardTitle>
              </CardHeader>
              <CardContent>
                <ImageUploadComponent imageType="exercise_image" title="Exercise" />
              </CardContent>
            </Card>

            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Exercise Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Exercise Name *</Label>
                  <Input
                    value={exerciseData.exercise_name}
                    onChange={(e) => setExerciseData(prev => ({ ...prev, exercise_name: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    required
                  />
                </div>

                <div>
                  <Label className="text-gray-300">Exercise Type *</Label>
                  <Select 
                    value={exerciseData.exercise_type} 
                    onValueChange={(value) => setExerciseData(prev => ({ ...prev, exercise_type: value }))}
                  >
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue placeholder="Select exercise type" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-700 border-gray-600">
                      {exerciseTypes.map((type) => (
                        <SelectItem key={type} value={type} className="text-white focus:bg-gray-600">
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-gray-300">Description *</Label>
                  <Textarea
                    value={exerciseData.exercise_description}
                    onChange={(e) => setExerciseData(prev => ({ ...prev, exercise_description: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                    required
                  />
                </div>

                <div>
                  <Label className="text-gray-300">Location *</Label>
                  <Input
                    value={exerciseData.location}
                    onChange={(e) => setExerciseData(prev => ({ ...prev, location: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    required
                  />
                </div>

                <div className="grid grid-cols-3 gap-4 items-start">
                  <div>
                    <Label className="text-gray-300 block mb-1">
                      Latitude *
                      <span className="text-xs text-gray-400 ml-1 block">(Decimal: -90 to 90)</span>
                    </Label>
                    <Input
                      type="text"
                      placeholder="45.0000"
                      value={exerciseData.latitude}
                      onChange={(e) => handleLatitudeChange(e.target.value, 'exercise')}
                      className={`bg-gray-700 border-gray-600 text-white ${
                        exerciseValidationErrors.latitude ? 'border-red-500 border-2' : ''
                      }`}
                      required
                    />
                    {exerciseValidationErrors.latitude && (
                      <p className="text-red-400 text-xs mt-1">{exerciseValidationErrors.latitude}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-gray-300 block mb-1">
                      Longitude *
                      <span className="text-xs text-gray-400 ml-1 block">(Decimal: -180 to 180)</span>
                    </Label>
                    <Input
                      type="text"
                      placeholder="-97.0000"
                      value={exerciseData.longitude}
                      onChange={(e) => handleLongitudeChange(e.target.value, 'exercise')}
                      className={`bg-gray-700 border-gray-600 text-white ${
                        exerciseValidationErrors.longitude ? 'border-red-500 border-2' : ''
                      }`}
                      required
                    />
                    {exerciseValidationErrors.longitude && (
                      <p className="text-red-400 text-xs mt-1">{exerciseValidationErrors.longitude}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-gray-300 block mb-1">
                      Zoom Level *
                      <span className="text-xs text-gray-400 ml-1 block">(Range: 1-20)</span>
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      max="20"
                      step="0.1"
                      placeholder="8.0"
                      value={exerciseData.zoom_level || ''}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value) && value >= 1 && value <= 20) {
                          setExerciseData(prev => ({ ...prev, zoom_level: value }));
                        } else if (e.target.value === '') {
                          setExerciseData(prev => ({ ...prev, zoom_level: '' }));
                        }
                      }}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Start Date *</Label>
                    <Input
                      type="date"
                      value={exerciseData.start_date}
                      onChange={(e) => setExerciseData(prev => ({ ...prev, start_date: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Start Time</Label>
                    <Input
                      type="text"
                      pattern="[0-2][0-9]:[0-5][0-9]"
                      placeholder="HH:MM (24-hour)"
                      maxLength="5"
                      value={exerciseData.start_time}
                      onChange={(e) => setExerciseData(prev => ({ ...prev, start_time: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">End Date *</Label>
                    <Input
                      type="date"
                      value={exerciseData.end_date}
                      onChange={(e) => setExerciseData(prev => ({ ...prev, end_date: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">End Time</Label>
                    <Input
                      type="text"
                      pattern="[0-2][0-9]:[0-5][0-9]"
                      placeholder="HH:MM (24-hour)"
                      maxLength="5"
                      value={exerciseData.end_time}
                      onChange={(e) => setExerciseData(prev => ({ ...prev, end_time: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ExCon Team Card */}
            <Card className="bg-gray-800 border-orange-500/50">
              <CardHeader>
                <CardTitle className="text-orange-500">ExCon Team (Exercise Control)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">ExCon Lead</Label>
                  <Select
                    value={exerciseData.excon_lead}
                    onValueChange={(value) => setExerciseData(prev => ({ ...prev, excon_lead: value }))}
                  >
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white" data-testid="excon-lead-select">
                      <SelectValue placeholder="Select ExCon Lead" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-700 border-gray-600">
                      {exconTeamMembers.length > 0 ? (
                        exconTeamMembers.map(member => (
                          <SelectItem key={member.id} value={member.name} className="text-white">
                            {member.name} — {member.position}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="_none" disabled className="text-gray-400">
                          No Directors, Evaluators, or Observers assigned
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-gray-500">
                  The ExCon Lead is responsible for managing exercise injections and controlling the flow of the exercise.
                </p>

                {/* ExCon Team Members from Participants */}
                <div className="border-t border-gray-700 pt-4" data-testid="excon-team-list">
                  <Label className="text-gray-300 mb-2 block">Assigned ExCon Personnel</Label>
                  <p className="text-xs text-gray-500 mb-3">
                    Participants assigned to this HIRA's exercises with position: Exercise Director, Exercise Evaluator, or Exercise Observer.
                  </p>
                  {exconTeamLoading ? (
                    <p className="text-gray-500 text-sm">Loading team members...</p>
                  ) : exconTeamMembers.length > 0 ? (
                    <div className="space-y-2">
                      {exconTeamMembers.map(member => (
                        <div
                          key={member.id}
                          className="flex items-center justify-between bg-gray-700/50 rounded-lg px-3 py-2 border border-gray-600/50"
                          data-testid={`excon-team-member-${member.id}`}
                        >
                          <div className="flex items-center space-x-3">
                            <div className="bg-orange-500/20 p-1.5 rounded-full">
                              <Users className="h-4 w-4 text-orange-400" />
                            </div>
                            <div>
                              <span className="text-white text-sm font-medium">{member.name}</span>
                              {member.email && (
                                <span className="text-gray-500 text-xs ml-2">{member.email}</span>
                              )}
                            </div>
                          </div>
                          <span className="text-xs bg-orange-500/20 text-orange-300 border border-orange-500/30 rounded px-2 py-0.5">
                            {member.position}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-gray-700/30 rounded-lg p-3 text-center">
                      <Users className="h-5 w-5 text-gray-600 mx-auto mb-1" />
                      <p className="text-gray-500 text-xs">
                        No Exercise Directors, Evaluators, or Observers assigned to this HIRA's exercises yet.
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 2: // Scope - same as Exercise Builder Step 2
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Exercise Scope</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Scope Description</Label>
                  <Textarea
                    value={exerciseData.scope_description}
                    onChange={(e) => setExerciseData(prev => ({ ...prev, scope_description: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                    placeholder="The scope defines the boundaries of the exercise—what is included and what falls outside its focus"
                  />
                </div>

                <div>
                  <Label className="text-gray-300">Organizations</Label>
                  <Textarea
                    value={exerciseData.scope_organizations}
                    onChange={(e) => setExerciseData(prev => ({ ...prev, scope_organizations: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={2}
                    placeholder="Which teams, agencies that will take part"
                  />
                </div>

                <div>
                  <Label className="text-gray-300">Personnel</Label>
                  <Textarea
                    value={exerciseData.scope_personnel}
                    onChange={(e) => setExerciseData(prev => ({ ...prev, scope_personnel: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={2}
                    placeholder="Which key individuals will take part"
                  />
                </div>

              </CardContent>
            </Card>
          </div>
        );

      case 3: // Purpose
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Exercise Purpose</CardTitle>
              </CardHeader>
              <CardContent>
                <div>
                  <Label className="text-gray-300">Purpose Description *</Label>
                  <Textarea
                    value={exerciseData.purpose_description}
                    onChange={(e) => setExerciseData(prev => ({ ...prev, purpose_description: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={4}
                    placeholder="Describe the overall purpose and intent of this exercise..."
                    required
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 4: // Scenario
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Scenario Image</CardTitle>
              </CardHeader>
              <CardContent>
                <ImageUploadComponent imageType="scenario_image" title="Scenario" />
              </CardContent>
            </Card>

            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Scenario Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Scenario Name *</Label>
                  <Input
                    value={exerciseData.scenario_name}
                    onChange={(e) => setExerciseData(prev => ({ ...prev, scenario_name: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    required
                  />
                </div>

                <div>
                  <Label className="text-gray-300">Scenario Description</Label>
                  <Textarea
                    value={exerciseData.scenario_description}
                    onChange={(e) => setExerciseData(prev => ({ ...prev, scenario_description: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={4}
                    placeholder="Scenario is an outline or model of a simulated sequence of events"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 5: // Goals - same pattern as Exercise Builder
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Exercise Goals</CardTitle>
                <CardDescription className="text-gray-400">
                  Define the high-level goals for this exercise
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Goal Name *</Label>
                  <Input
                    value={currentGoal.name}
                    onChange={(e) => setCurrentGoal(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Test emergency notification systems"
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Goal Description</Label>
                  <Textarea
                    value={currentGoal.description}
                    onChange={(e) => setCurrentGoal(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="A goal is a broad, high-level statement of what the exercise intends to achieve"
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={addGoal} className="bg-orange-500 hover:bg-orange-600">
                    <Plus className="h-4 w-4 mr-2" />
                    {editingGoalId ? 'Update Goal' : 'Add Goal'}
                  </Button>
                  {editingGoalId && (
                    <Button onClick={cancelGoalEdit} variant="outline" className="border-gray-500">
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {goals.length > 0 && (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-orange-500">Added Goals ({goals.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {goals.map((goal) => (
                      <div key={goal.id} className="flex items-center justify-between bg-gray-700/50 p-3 rounded-lg">
                        <div className="flex-1">
                          <h4 className="text-white font-medium">{goal.name}</h4>
                          {goal.description && <p className="text-gray-400 text-sm">{goal.description}</p>}
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => editGoal(goal)} className="text-blue-400">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteGoal(goal.id)} className="text-red-400">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        );

      case 6: // Objectives
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Exercise Objectives</CardTitle>
                <CardDescription className="text-gray-400">
                  Define specific, measurable objectives for this exercise
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Objective Name *</Label>
                  <Input
                    value={currentObjective.name}
                    onChange={(e) => setCurrentObjective(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Activate EOC within 30 minutes"
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Objective Description</Label>
                  <Textarea
                    value={currentObjective.description}
                    onChange={(e) => setCurrentObjective(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="An objective is a specific, measurable action"
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={addObjective} className="bg-orange-500 hover:bg-orange-600">
                    <Plus className="h-4 w-4 mr-2" />
                    {editingObjectiveId ? 'Update Objective' : 'Add Objective'}
                  </Button>
                  {editingObjectiveId && (
                    <Button onClick={cancelObjectiveEdit} variant="outline" className="border-gray-500">
                      <X className="h-4 w-4 mr-2" />Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {objectives.length > 0 && (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-orange-500">Added Objectives ({objectives.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {objectives.map((obj) => (
                      <div key={obj.id} className="flex items-center justify-between bg-gray-700/50 p-3 rounded-lg">
                        <div className="flex-1">
                          <h4 className="text-white font-medium">{obj.name}</h4>
                          {obj.description && <p className="text-gray-400 text-sm">{obj.description}</p>}
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => editObjective(obj)} className="text-blue-400">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteObjective(obj.id)} className="text-red-400">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        );

      case 7: // Functions
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Functions to be Exercised</CardTitle>
                <CardDescription className="text-gray-400">
                  Define the functions and capabilities to be tested
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Function Name *</Label>
                  <Input
                    value={currentFunction.name}
                    onChange={(e) => setCurrentFunction(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Emergency Communications"
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Function Description</Label>
                  <Textarea
                    value={currentFunction.description}
                    onChange={(e) => setCurrentFunction(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe the function"
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={addFunction} className="bg-orange-500 hover:bg-orange-600">
                    <Plus className="h-4 w-4 mr-2" />
                    {editingFunctionId ? 'Update Function' : 'Add Function'}
                  </Button>
                  {editingFunctionId && (
                    <Button onClick={cancelFunctionEdit} variant="outline" className="border-gray-500">
                      <X className="h-4 w-4 mr-2" />Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {functions.length > 0 && (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-orange-500">Added Functions ({functions.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {functions.map((func) => (
                      <div key={func.id} className="flex items-center justify-between bg-gray-700/50 p-3 rounded-lg">
                        <div className="flex-1">
                          <h4 className="text-white font-medium">{func.name}</h4>
                          {func.description && <p className="text-gray-400 text-sm">{func.description}</p>}
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => editFunction(func)} className="text-blue-400">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteFunction(func.id)} className="text-red-400">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        );

      case 8: // Injections (MSEL) - PRIMARY EXCON CONTROL - Same as Exercise Builder
        return (
          <div className="space-y-6">
            <div className="bg-orange-500/10 border border-orange-500/50 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Syringe className="h-6 w-6 text-orange-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-orange-500 font-bold">Primary Exercise Control</h3>
                  <p className="text-gray-300 text-sm">
                    Master Scenario Events List (MSEL) - Exercise event planning and tracking.
                    Each injection introduces new information or events that drive participant actions.
                  </p>
                </div>
              </div>
            </div>

            {/* Event Identification Card */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Event Identification</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Event Number *</Label>
                    <Input
                      type="number"
                      min="1"
                      value={currentInjection.event_number}
                      onChange={(e) => setCurrentInjection(prev => ({ ...prev, event_number: parseInt(e.target.value) || 1 }))}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Designated Scenario Time *</Label>
                    <Input
                      value={currentInjection.scenario_time}
                      onChange={(e) => setCurrentInjection(prev => ({ ...prev, scenario_time: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white"
                      placeholder="e.g., 09:30, T+15 minutes, Day 1 - 14:00"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Event Classification Card */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Event Classification</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Event Type *</Label>
                  <Select 
                    value={currentInjection.event_type} 
                    onValueChange={(value) => setCurrentInjection(prev => ({ ...prev, event_type: value }))}
                  >
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-700 border-gray-600">
                      {eventTypes.map((type) => (
                        <SelectItem key={type} value={type} className="text-white focus:bg-gray-600">
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-gray-300">Inject Mode *</Label>
                  <Select 
                    value={currentInjection.inject_mode} 
                    onValueChange={(value) => setCurrentInjection(prev => ({ ...prev, inject_mode: value }))}
                  >
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue placeholder="Select inject mode" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-700 border-gray-600">
                      {injectModes.map((mode) => (
                        <SelectItem key={mode} value={mode} className="text-white focus:bg-gray-600">
                          {mode}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Communication Details Card */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Communication Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">From (Controller/Entity) *</Label>
                  <Select 
                    value={currentInjection.from_entity} 
                    onValueChange={(value) => setCurrentInjection(prev => ({ ...prev, from_entity: value }))}
                  >
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue placeholder="Select controller role" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-700 border-gray-600">
                      {controllerRoles.map((role) => (
                        <SelectItem key={role} value={role} className="text-white focus:bg-gray-600">
                          {role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-400 mt-1">Or manually edit the field above to enter a custom value</p>
                </div>

                <div>
                  <Label className="text-gray-300">To (Player/Group) *</Label>
                  <Select 
                    value={currentInjection.to_entity} 
                    onValueChange={(value) => setCurrentInjection(prev => ({ ...prev, to_entity: value }))}
                  >
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue placeholder="Select player role" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-700 border-gray-600">
                      {playerRoles.map((role) => (
                        <SelectItem key={role} value={role} className="text-white focus:bg-gray-600">
                          {role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-400 mt-1">Or manually edit the field above to enter a custom value</p>
                </div>
              </CardContent>
            </Card>

            {/* Event Content & Objectives Card */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Event Content & Objectives</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Message *</Label>
                  <Textarea
                    value={currentInjection.message}
                    onChange={(e) => setCurrentInjection(prev => ({ ...prev, message: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={4}
                    placeholder="The core content of the inject - information, request, or call to action"
                  />
                </div>

                <div>
                  <Label className="text-gray-300">Expected Participant Response *</Label>
                  <Textarea
                    value={currentInjection.expected_response}
                    onChange={(e) => setCurrentInjection(prev => ({ ...prev, expected_response: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                    placeholder="The anticipated action or reaction from the player"
                  />
                </div>

                <div>
                  <Label className="text-gray-300">Objective, Capability, and/or Critical Task *</Label>
                  <Textarea
                    value={currentInjection.objective_capability_task}
                    onChange={(e) => setCurrentInjection(prev => ({ ...prev, objective_capability_task: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                    placeholder="The specific exercise objective or critical task this event tests"
                  />
                </div>

                <div>
                  <Label className="text-gray-300">Notes</Label>
                  <Textarea
                    value={currentInjection.notes}
                    onChange={(e) => setCurrentInjection(prev => ({ ...prev, notes: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={2}
                    placeholder="Additional controller information or details"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Add/Update Button */}
            <div className="flex gap-2">
              <Button onClick={addInjection} className="bg-orange-500 hover:bg-orange-600">
                <Plus className="h-4 w-4 mr-2" />
                {editingInjectionId ? 'Update Event' : 'Add Event'}
              </Button>
              {editingInjectionId && (
                <Button onClick={cancelInjectionEdit} variant="outline" className="border-gray-500">
                  <X className="h-4 w-4 mr-2" />Cancel
                </Button>
              )}
            </div>

            {/* MSEL Events List */}
            {injections.length > 0 && (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-orange-500">MSEL Events ({injections.length})</CardTitle>
                  <CardDescription className="text-gray-400">
                    Master Scenario Events List - Click to edit or delete events
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {injections.sort((a, b) => (a.event_number || 0) - (b.event_number || 0)).map((inj) => (
                      <div key={inj.id} className="bg-gray-700/50 p-4 rounded-lg border border-gray-600">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <span className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded">
                              #{inj.event_number}
                            </span>
                            <span className="text-white font-medium">{inj.event_type || 'No Type'}</span>
                            {inj.scenario_time && (
                              <span className="text-gray-400 text-sm">@ {inj.scenario_time}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {inj.inject_mode && (
                              <span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-400">
                                {inj.inject_mode}
                              </span>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => editInjection(inj)} className="text-blue-400">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteInjection(inj.id)} className="text-red-400">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        <div className="flex gap-4 text-xs text-gray-400 mb-2">
                          {inj.from_entity && <span><strong>From:</strong> {inj.from_entity}</span>}
                          {inj.to_entity && <span><strong>To:</strong> {inj.to_entity}</span>}
                        </div>
                        
                        {inj.message && (
                          <p className="text-gray-300 text-sm mb-2 line-clamp-2">{inj.message}</p>
                        )}
                        
                        {inj.expected_response && (
                          <p className="text-gray-400 text-xs">
                            <strong>Expected Response:</strong> {inj.expected_response}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        );

      case 9: // Organizations - Same as Exercise Builder Step 9
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Exercise Organizations</CardTitle>
                <CardDescription className="text-gray-400">
                  Add organizations participating in the exercise
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Organization Image Upload */}
                <div className="p-4 bg-gray-700/50 rounded-lg">
                  <Label className="text-gray-300 mb-3 block">Organization Image</Label>
                  <div className="flex items-center space-x-6">
                    <div
                      {...exconOrgImageDropProps}
                      onClick={() => document.getElementById('organization-image-upload')?.click()}
                      data-testid="excon-org-image-dropzone"
                      title="Click to browse or drop an image here"
                      className={`w-24 h-24 rounded-lg bg-gray-700 border-2 border-dashed flex items-center justify-center overflow-hidden cursor-pointer transition-colors ${
                        isExconOrgImageDragging ? 'border-orange-500 bg-orange-500/10 ring-2 ring-orange-500' : 'border-gray-600 hover:border-orange-500/50'
                      }`}
                    >
                      {organizationImagePreview ? (
                        <img src={organizationImagePreview} alt="Organization" className="w-full h-full object-cover rounded-lg" />
                      ) : (
                        <div className="text-center px-1">
                          <Camera className="h-6 w-6 text-gray-400 mx-auto mb-1" />
                          <span className="text-xs text-gray-400">{isExconOrgImageDragging ? 'Drop' : 'Drop here'}</span>
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <div className="flex space-x-2">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleOrganizationImageUpload}
                          className="hidden"
                          id="organization-image-upload"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById('organization-image-upload').click()}
                          className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Upload Image
                        </Button>
                      </div>
                      <p className="text-xs text-gray-400">
                        Add a logo or photo of the organization
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-gray-300">Organization Name *</Label>
                  <Input
                    value={currentOrganization.name}
                    onChange={(e) => setCurrentOrganization(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., City Emergency Services"
                    className="bg-gray-700 border-gray-600 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Description</Label>
                  <Textarea
                    value={currentOrganization.description}
                    onChange={(e) => setCurrentOrganization(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe the organization's role..."
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={2}
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Home Base</Label>
                  <Input
                    value={currentOrganization.home_base}
                    onChange={(e) => setCurrentOrganization(prev => ({ ...prev, home_base: e.target.value }))}
                    placeholder="Organization headquarters or base location"
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Latitude</Label>
                    <Input
                      type="text"
                      value={currentOrganization.latitude}
                      onChange={(e) => handleLatitudeChange(e.target.value, 'organization')}
                      placeholder="45.1234"
                      className={`bg-gray-700 border-gray-600 text-white ${
                        organizationValidationErrors.latitude ? 'border-red-500' : ''
                      }`}
                    />
                    {organizationValidationErrors.latitude && (
                      <p className="text-red-500 text-sm mt-1">{organizationValidationErrors.latitude}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-gray-300">Longitude</Label>
                    <Input
                      type="text"
                      value={currentOrganization.longitude}
                      onChange={(e) => handleLongitudeChange(e.target.value, 'organization')}
                      placeholder="-97.0000"
                      className={`bg-gray-700 border-gray-600 text-white ${
                        organizationValidationErrors.longitude ? 'border-red-500' : ''
                      }`}
                    />
                    {organizationValidationErrors.longitude && (
                      <p className="text-red-500 text-sm mt-1">{organizationValidationErrors.longitude}</p>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Contact First Name</Label>
                    <Input
                      value={currentOrganization.contact_first_name}
                      onChange={(e) => setCurrentOrganization(prev => ({ ...prev, contact_first_name: e.target.value }))}
                      placeholder="First name"
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Contact Last Name</Label>
                    <Input
                      value={currentOrganization.contact_last_name}
                      onChange={(e) => setCurrentOrganization(prev => ({ ...prev, contact_last_name: e.target.value }))}
                      placeholder="Last name"
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Contact Cell Phone</Label>
                    <Input
                      value={currentOrganization.contact_phone}
                      onChange={(e) => setCurrentOrganization(prev => ({ ...prev, contact_phone: e.target.value }))}
                      placeholder="+1 (555) 123-4567"
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Contact Email</Label>
                    <Input
                      type="email"
                      value={currentOrganization.contact_email}
                      onChange={(e) => setCurrentOrganization(prev => ({ ...prev, contact_email: e.target.value }))}
                      placeholder="contact@organization.com"
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                </div>

                <div className="flex space-x-3">
                  <Button 
                    onClick={addOrganization}
                    className={editingOrganizationId ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700"}
                  >
                    {editingOrganizationId ? (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Update Organization
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Organization
                      </>
                    )}
                  </Button>
                  {editingOrganizationId && (
                    <Button 
                      onClick={cancelOrganizationEdit}
                      variant="outline"
                      className="border-gray-500 text-gray-300 hover:bg-gray-700"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Added Organizations Display */}
            {organizations.length > 0 && (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-orange-500">Added Organizations ({organizations.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {organizations.map((org) => (
                      <div key={org.id} className="p-4 bg-gray-700 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div className="flex items-start space-x-4 flex-1">
                            {/* Organization Image */}
                            <div className="w-16 h-16 rounded-lg bg-gray-600 flex-shrink-0 overflow-hidden">
                              {org.organization_image ? (
                                <img src={org.organization_image} alt={org.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Building className="h-8 w-8 text-gray-400" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-white">{org.name}</h3>
                              <p className="text-gray-300 text-sm">{org.description}</p>
                              <div className="mt-2 text-xs text-gray-400 space-y-1">
                                {org.home_base && <div>Home Base: {org.home_base}</div>}
                                {(org.contact_first_name || org.contact_last_name) && (
                                  <div>Contact: {org.contact_first_name} {org.contact_last_name}</div>
                                )}
                                {(org.contact_phone || org.contact_email) && (
                                  <div>
                                    {org.contact_phone && <>Phone: {org.contact_phone}</>}
                                    {org.contact_phone && org.contact_email && ' | '}
                                    {org.contact_email && <>Email: {org.contact_email}</>}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              onClick={() => editOrganization(org)}
                              variant="ghost"
                              size="sm"
                              className="text-blue-400 hover:text-blue-300"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => deleteOrganization(org.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        );

      case 10: // Team Coordinators
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Team Coordinators</CardTitle>
                <CardDescription className="text-gray-400">
                  Select participants who will serve as team coordinators
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-green-900/20 border border-green-500/30 rounded">
                  <div className="flex items-center space-x-3">
                    <Users className="h-8 w-8 text-green-400" />
                    <div>
                      <h3 className="text-green-300 font-semibold">Participant Integration</h3>
                      <p className="text-gray-400 text-sm">Import eligible participants with coordinator roles</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                    onClick={loadCoordinators}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Load Coordinators'}
                  </Button>
                </div>
                <div className="bg-gray-700/50 p-4 rounded">
                  <p className="text-gray-400 text-sm">
                    Coordinators will be automatically selected from participants matching these criteria:
                  </p>
                  <ul className="text-gray-400 text-sm mt-2 space-y-1">
                    <li>• Position equals "Team Coordinator"</li>
                    <li>• Assigned Exercise equals the current exercise</li>
                    <li>• "Participant is involved in the current exercise" checkbox is checked</li>
                  </ul>
                </div>
                
                {/* Display loaded coordinators */}
                {coordinators.length > 0 && (
                  <Card className="bg-gray-700/50 border-gray-600">
                    <CardHeader>
                      <CardTitle className="text-green-400 text-lg">Loaded Coordinators</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {coordinators.map((coordinator, index) => (
                          <div key={coordinator.id || index} className="bg-gray-800 p-4 rounded border border-gray-600">
                            <div className="flex justify-between items-start">
                              <div className="space-y-2">
                                <h4 className="font-semibold text-white">{coordinator.name}</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-300">
                                  <div><span className="text-gray-400">Role:</span> {coordinator.role}</div>
                                  <div><span className="text-gray-400">Email:</span> {coordinator.email || 'N/A'}</div>
                                  <div><span className="text-gray-400">Phone:</span> {coordinator.phone || 'N/A'}</div>
                                  <div><span className="text-gray-400">Assigned to:</span> {coordinator.assignedTo || 'N/A'}</div>
                                </div>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-400 border-red-400 hover:bg-red-400 hover:text-white"
                                onClick={() => {
                                  setCoordinators(prev => prev.filter((_, i) => i !== index));
                                }}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 text-sm text-gray-400">
                        Total coordinators: {coordinators.length}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>
          </div>
        );

      case 11: // Code Words
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Exercise Code Words</CardTitle>
                <CardDescription className="text-gray-400">
                  Define code words used during the exercise
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Code Word *</Label>
                  <Input
                    value={currentCodeWord.word}
                    onChange={(e) => setCurrentCodeWord(prev => ({ ...prev, word: e.target.value }))}
                    placeholder="e.g., THUNDERBOLT"
                    className="bg-gray-700 border-gray-600 text-white"
                    data-testid="code-word-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Definition</Label>
                  <Textarea
                    value={currentCodeWord.meaning}
                    onChange={(e) => setCurrentCodeWord(prev => ({ ...prev, meaning: e.target.value }))}
                    placeholder="Define what this code word means..."
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                    data-testid="code-word-meaning-input"
                  />
                </div>
                <div className="flex space-x-3">
                  <Button 
                    onClick={addCodeWord}
                    className={editingCodeWordId ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700"}
                    data-testid="add-code-word-btn"
                  >
                    {editingCodeWordId ? (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Update Code Word
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Code Word
                      </>
                    )}
                  </Button>
                  {editingCodeWordId && (
                    <Button 
                      onClick={cancelCodeWordEdit}
                      variant="outline"
                      className="border-gray-500 text-gray-300 hover:bg-gray-700"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Added Code Words Display */}
            {codeWords.length > 0 && (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-orange-500">Added Code Words ({codeWords.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {codeWords.map((codeWord) => (
                      <div key={codeWord.id} className="p-4 bg-gray-700 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-semibold text-white text-lg">{codeWord.word}</h3>
                            <p className="text-gray-300 text-sm mt-1">{codeWord.meaning}</p>
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              onClick={() => editCodeWord(codeWord)}
                              variant="ghost"
                              size="sm"
                              className="text-blue-400 hover:text-blue-300"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => deleteCodeWord(codeWord.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        );

      case 12: // Callsigns
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Communication Callsigns</CardTitle>
                <CardDescription className="text-gray-400">
                  Define radio callsigns for the exercise
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Callsign *</Label>
                  <Input
                    value={currentCallsign.callsign}
                    onChange={(e) => setCurrentCallsign(prev => ({ ...prev, callsign: e.target.value }))}
                    placeholder="e.g., COMMAND-1"
                    className="bg-gray-700 border-gray-600 text-white"
                    data-testid="callsign-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Definition</Label>
                  <Textarea
                    value={currentCallsign.description}
                    onChange={(e) => setCurrentCallsign(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Define the role or position for this callsign..."
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                    data-testid="callsign-description-input"
                  />
                </div>
                <div className="flex space-x-3">
                  <Button 
                    onClick={addCallsign}
                    className={editingCallsignId ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700"}
                    data-testid="add-callsign-btn"
                  >
                    {editingCallsignId ? (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Update Callsign
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Callsign
                      </>
                    )}
                  </Button>
                  {editingCallsignId && (
                    <Button 
                      onClick={cancelCallsignEdit}
                      variant="outline"
                      className="border-gray-500 text-gray-300 hover:bg-gray-700"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Added Callsigns Display */}
            {callsigns.length > 0 && (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-orange-500">Added Callsigns ({callsigns.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {callsigns.map((callsign) => (
                      <div key={callsign.id} className="p-4 bg-gray-700 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-semibold text-white text-lg">{callsign.callsign}</h3>
                            <p className="text-gray-300 text-sm mt-1">{callsign.description}</p>
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              onClick={() => editCallsign(callsign)}
                              variant="ghost"
                              size="sm"
                              className="text-blue-400 hover:text-blue-300"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => deleteCallsign(callsign.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        );

      case 13: // Assumptions
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Exercise Assumptions</CardTitle>
                <CardDescription className="text-gray-400">
                  Define assumptions made for the exercise
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Assumption Name *</Label>
                  <Input
                    value={currentAssumption.assumption || ''}
                    onChange={(e) => setCurrentAssumption(prev => ({ ...prev, assumption: e.target.value }))}
                    placeholder="e.g., Weather Conditions"
                    className="bg-gray-700 border-gray-600 text-white"
                    data-testid="assumption-name-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Assumption Description</Label>
                  <Textarea
                    value={currentAssumption.description || ''}
                    onChange={(e) => setCurrentAssumption(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Assumptions are assertions that are accepted as true or certain to happen without proof to allow the scenario and the exercise to proceed as planned. They serve to create a common baseline understanding of the operating environment for all participants and prevent the exercise from getting sidetracked by uncontrollable or external factors."
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                  />
                </div>
                <div className="flex space-x-3">
                  <Button 
                    onClick={addAssumption}
                    className={editingAssumptionId ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700"}
                    data-testid="add-assumption-btn"
                  >
                    {editingAssumptionId ? (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Update Assumption
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Assumption
                      </>
                    )}
                  </Button>
                  {editingAssumptionId && (
                    <Button 
                      onClick={cancelAssumptionEdit}
                      variant="outline"
                      className="border-gray-500 text-gray-300 hover:bg-gray-700"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Added Assumptions Display */}
            {assumptions.length > 0 && (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-orange-500">Added Assumptions ({assumptions.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {assumptions.map((assumption) => (
                      <div key={assumption.id} className="p-4 bg-gray-700 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-semibold text-white text-lg">{assumption.assumption}</h3>
                            <p className="text-gray-300 text-sm mt-1">{assumption.description}</p>
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              onClick={() => editAssumption(assumption)}
                              variant="ghost"
                              size="sm"
                              className="text-blue-400 hover:text-blue-300"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => deleteAssumption(assumption.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        );

      case 14: // Artificialities
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Exercise Artificialities</CardTitle>
                <CardDescription className="text-gray-400">
                  Define artificial constraints or limitations for the exercise
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Artificiality Name *</Label>
                  <Input
                    value={currentArtificiality.artificiality || ''}
                    onChange={(e) => setCurrentArtificiality(prev => ({ ...prev, artificiality: e.target.value }))}
                    placeholder="e.g., Simulated Resource Limitations"
                    className="bg-gray-700 border-gray-600 text-white"
                    data-testid="artificiality-name-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Artificiality Description</Label>
                  <Textarea
                    value={currentArtificiality.description || ''}
                    onChange={(e) => setCurrentArtificiality(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe the artificial constraint or limitation..."
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                  />
                </div>
                <div className="flex space-x-3">
                  <Button 
                    onClick={addArtificiality}
                    className={editingArtificialityId ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700"}
                    data-testid="add-artificiality-btn"
                  >
                    {editingArtificialityId ? (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Update Artificiality
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Artificiality
                      </>
                    )}
                  </Button>
                  {editingArtificialityId && (
                    <Button 
                      onClick={cancelArtificialityEdit}
                      variant="outline"
                      className="border-gray-500 text-gray-300 hover:bg-gray-700"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Added Artificialities Display */}
            {artificialities.length > 0 && (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-orange-500">Added Artificialities ({artificialities.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {artificialities.map((artificiality) => (
                      <div key={artificiality.id} className="p-4 bg-gray-700 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-semibold text-white text-lg">{artificiality.artificiality}</h3>
                            <p className="text-gray-300 text-sm mt-1">{artificiality.description}</p>
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              onClick={() => editArtificiality(artificiality)}
                              variant="ghost"
                              size="sm"
                              className="text-blue-400 hover:text-blue-300"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => deleteArtificiality(artificiality.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        );

      case 15: // Safety
        return (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Safety Image</CardTitle>
              </CardHeader>
              <CardContent>
                <ImageUploadComponent imageType="safety_image" title="Safety" />
              </CardContent>
            </Card>

            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Safety Officer Information</CardTitle>
                <CardDescription className="text-gray-400">
                  Safety officer details are automatically populated from participants where position is "Command - Safety Officer", assigned to this exercise, and participating
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-green-900/20 border border-green-500/30 p-4 rounded">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <Shield className="h-6 w-6 text-green-400" />
                      <h3 className="text-green-300 font-semibold">Current Safety Officer</h3>
                    </div>
                    {safetyOfficerLoading && (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-400"></div>
                    )}
                  </div>
                  {safetyOfficer.participantId ? (
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <Label className="text-gray-300">First Name</Label>
                        <Input 
                          value={safetyOfficer.firstName}
                          placeholder="Not found"
                          className="bg-gray-700 border-gray-600 text-white"
                          readOnly
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Last Name</Label>
                        <Input 
                          value={safetyOfficer.lastName}
                          placeholder="Not found"
                          className="bg-gray-700 border-gray-600 text-white"
                          readOnly
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Cell Phone</Label>
                        <Input 
                          value={safetyOfficer.cellPhone}
                          placeholder="Not found"
                          className="bg-gray-700 border-gray-600 text-white"
                          readOnly
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <Label className="text-gray-300">First Name</Label>
                        <Input 
                          placeholder="No Safety Officer found"
                          className="bg-gray-700 border-gray-600 text-gray-500"
                          disabled
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Last Name</Label>
                        <Input 
                          placeholder="No Safety Officer found"
                          className="bg-gray-700 border-gray-600 text-gray-500"
                          disabled
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Cell Phone</Label>
                        <Input 
                          placeholder="No Safety Officer found"
                          className="bg-gray-700 border-gray-600 text-gray-500"
                          disabled
                        />
                      </div>
                    </div>
                  )}
                  <div className="mt-3 flex items-center space-x-3">
                    <Button 
                      variant="outline" 
                      className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                      onClick={loadSafetyOfficer}
                      disabled={safetyOfficerLoading}
                    >
                      {safetyOfficerLoading ? 'Loading...' : 'Load Safety Officer Details'}
                    </Button>
                    {!safetyOfficer.participantId && !safetyOfficerLoading && (
                      <p className="text-xs text-gray-400">
                        No participant found with position "Command - Safety Officer" assigned to this exercise
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Safety Concerns</CardTitle>
                <CardDescription className="text-gray-400">
                  Document safety concerns and considerations for the exercise
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Safety Concern Name *</Label>
                  <Input
                    value={currentSafetyConcern.concern || ''}
                    onChange={(e) => setCurrentSafetyConcern(prev => ({ ...prev, concern: e.target.value }))}
                    placeholder="e.g., Vehicle Operations"
                    className="bg-gray-700 border-gray-600 text-white"
                    data-testid="safety-concern-name-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Safety Concern Description</Label>
                  <Textarea
                    value={currentSafetyConcern.description || ''}
                    onChange={(e) => setCurrentSafetyConcern(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe the safety concern and mitigation measures..."
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Safety Officer Name</Label>
                    <Input
                      value={currentSafetyConcern.safety_officer || ''}
                      onChange={(e) => setCurrentSafetyConcern(prev => ({ ...prev, safety_officer: e.target.value }))}
                      placeholder="Responsible safety officer"
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Officer Phone</Label>
                    <Input
                      value={currentSafetyConcern.phone || ''}
                      onChange={(e) => setCurrentSafetyConcern(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+1 (555) 123-4567"
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                </div>
                <div className="flex space-x-3">
                  <Button 
                    onClick={addSafetyConcern}
                    className={editingSafetyConcernId ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}
                    data-testid="add-safety-concern-btn"
                  >
                    {editingSafetyConcernId ? (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Update Safety Concern
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Safety Concern
                      </>
                    )}
                  </Button>
                  {editingSafetyConcernId && (
                    <Button 
                      onClick={cancelSafetyConcernEdit}
                      variant="outline"
                      className="border-gray-500 text-gray-300 hover:bg-gray-700"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Added Safety Concerns Display */}
            {safetyConcerns.length > 0 && (
              <Card className="bg-gray-800 border-red-500/20">
                <CardHeader>
                  <CardTitle className="text-red-500">Added Safety Concerns ({safetyConcerns.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {safetyConcerns.map((concern) => (
                      <div key={concern.id} className="p-4 bg-gray-700 rounded-lg border-l-4 border-red-500">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <ShieldAlert className="h-5 w-5 text-red-400" />
                              <h3 className="font-semibold text-white text-lg">{concern.concern}</h3>
                            </div>
                            <p className="text-gray-300 text-sm mb-2">{concern.description}</p>
                            <div className="text-xs text-gray-400">
                              <span>Safety Officer: {concern.safety_officer || 'Not assigned'}</span>
                              {concern.phone && <span> | Phone: {concern.phone}</span>}
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              onClick={() => editSafetyConcern(concern)}
                              variant="ghost"
                              size="sm"
                              className="text-blue-400 hover:text-blue-300"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => deleteSafetyConcern(concern.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        );

      case 16: // Mapping
        return (
          <div className="space-y-6">
            {/* Info Banner */}
            <div className="bg-blue-900/20 border border-blue-500/30 p-4 rounded-lg">
              <div className="flex items-center space-x-3">
                <Map className="h-6 w-6 text-blue-400" />
                <div>
                  <h3 className="text-blue-300 font-semibold">Exercise Mapping</h3>
                  <p className="text-gray-400 text-sm">Create and manage map objects for your exercise. Add markers, zones, and routes that will be used during the exercise.</p>
                </div>
              </div>
            </div>

            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Mapping</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-700/50 rounded-lg p-8 text-center border-2 border-dashed border-gray-600">
                  <MapPin className="h-16 w-16 mx-auto text-gray-500 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-300 mb-2">Mapping Available in ICS</h3>
                  <p className="text-gray-400 text-sm mb-6">
                    Save this exercise to access full mapping capabilities in the ICS Dashboard.
                  </p>
                  
                  <Button
                    onClick={saveExercise}
                    disabled={loading || !exerciseData.exercise_name}
                    className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3"
                    data-testid="save-launch-ics-btn"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-5 w-5 mr-2" />
                        Save & Launch ICS
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={onBack}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to HIRA
            </Button>
            <div className="h-6 w-px bg-gray-700"></div>
            <div>
              <h1 className="text-2xl font-bold text-orange-500">ExCon Builder</h1>
              <p className="text-sm text-gray-400">Exercise Control - 16 Step Builder</p>
            </div>
          </div>
          {hiraEntry && (
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg px-4 py-2">
              <span className="text-orange-400 text-sm">
                Based on HIRA: <strong>{hiraEntry.name}</strong>
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="flex">
        {/* Step Sidebar */}
        <div className="w-72 bg-gray-800 border-r border-gray-700 min-h-[calc(100vh-80px)] p-4 overflow-y-auto">
          <h2 className="text-orange-500 font-bold text-lg mb-4">ExCon Builder</h2>
          <div className="space-y-1">
            {steps.map((step) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.number;
              const isCompleted = currentStep > step.number;
              const isInjections = step.number === 8;

              return (
                <button
                  key={step.number}
                  onClick={() => setCurrentStep(step.number)}
                  className={`w-full text-left p-2 rounded-lg transition-all ${
                    isActive
                      ? isInjections ? 'bg-orange-600 text-white' : 'bg-orange-500 text-white'
                      : isCompleted
                      ? 'bg-gray-700 text-green-400 hover:bg-gray-600'
                      : isInjections
                      ? 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 border border-orange-500/50'
                      : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      isActive
                        ? 'bg-white text-orange-500'
                        : isCompleted
                        ? 'bg-green-500 text-white'
                        : isInjections
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-600 text-gray-300'
                    }`}>
                      {isCompleted ? <Check className="h-3 w-3" /> : step.number}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{step.title}</div>
                    </div>
                    {isInjections && <Syringe className="h-4 w-4 text-orange-400" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            {/* Step Header */}
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-orange-500">
                Step {currentStep}: {steps[currentStep - 1].title}
              </h2>
              <p className="text-gray-400">{steps[currentStep - 1].description}</p>
            </div>

            {/* Step Content */}
            {renderStepContent()}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pb-8">
              <Button
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 1}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Previous
              </Button>

              {currentStep < steps.length ? (
                <Button
                  onClick={nextStep}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={saveExercise}
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {loading ? 'Saving...' : 'Save & Launch ICS'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExConBuilder;
